import React, { useCallback, useRef, useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import DOMPurify from 'dompurify'

import { Typography } from '~components/internal'
import Avatar from '~components/internal/Avatar'
import Chip from '~components/internal/Chip'
import Icon from '~components/internal/Icon'
import { PROSE_CONTENT_STYLES } from '~components/internal/RichTextEditor'
import { typography } from '~tokens/internal'

import './EmailPreview.scss'

export interface EmailPreviewProps {
  subject: string
  body: string
  orgName?: string
  fromEmail?: string
  orgLogoUrl?: string
  showEmailTemplate?: boolean
  renderedHtml?: string
  isLoading?: boolean
  recipients?: string[]
  ccRecipients?: string[]
  bccRecipients?: string[]
  attachmentNames?: string[]
}

const EmailPreview: React.FC<EmailPreviewProps> = ({
  subject,
  body,
  orgName = 'Your Organization',
  fromEmail = 'noreply@polimorphic.com',
  orgLogoUrl,
  showEmailTemplate = false,
  renderedHtml,
  isLoading = false,
  recipients,
  ccRecipients,
  bccRecipients,
  attachmentNames,
}) => {
  const frameRef = useRef<HTMLIFrameElement>(null)
  const [frameHeight, setFrameHeight] = useState(480)

  const handleFrameLoad = useCallback(() => {
    const frameBody = frameRef.current?.contentDocument?.body
    if (frameBody) {
      setFrameHeight(frameBody.scrollHeight)
    }
  }, [])

  return (
    <Box className='email-preview'>
      <Box className='email-preview__chrome-bar'>
        <Box className='email-preview__chrome-dots'>
          <Box className='email-preview__chrome-dot email-preview__chrome-dot--red' />
          <Box className='email-preview__chrome-dot email-preview__chrome-dot--yellow' />
          <Box className='email-preview__chrome-dot email-preview__chrome-dot--green' />
        </Box>
        <Box className='email-preview__chrome-address'>
          <Typography className='email-preview__chrome-label'>Mail</Typography>
        </Box>
      </Box>

      <Box className='email-preview__header'>
        <Box className='email-preview__header-row'>
          <Typography className='email-preview__subject'>{subject || 'No subject'}</Typography>
          <Typography className='email-preview__timestamp'>Just now</Typography>
        </Box>
        {fromEmail ? (
          <Box className='email-preview__sender-row'>
            <Typography className='email-preview__sender-name'>{orgName}</Typography>
            <Typography className='email-preview__sender-email'>&lt;{fromEmail}&gt;</Typography>
          </Box>
        ) : (
          <Typography className='email-preview__recipient-row'>From: </Typography>
        )}
        <Typography className='email-preview__recipient-row'>
          To:{' '}
          {recipients && recipients.length > 0 && (
            <span className='email-preview__recipient-name'>{recipients.join(', ')}</span>
          )}
        </Typography>
        {ccRecipients && ccRecipients.length > 0 && (
          <Typography className='email-preview__recipient-row'>
            Cc: <span className='email-preview__recipient-name'>{ccRecipients.join(', ')}</span>
          </Typography>
        )}
        {bccRecipients && bccRecipients.length > 0 && (
          <Typography className='email-preview__recipient-row'>
            Bcc: <span className='email-preview__recipient-name'>{bccRecipients.join(', ')}</span>
          </Typography>
        )}
      </Box>

      {renderedHtml ? (
        <Box className='email-preview__body email-preview__body--rendered'>
          <iframe
            ref={frameRef}
            title='Email preview'
            className='email-preview__rendered-frame'
            sandbox='allow-same-origin'
            srcDoc={renderedHtml}
            style={{ height: frameHeight }}
            onLoad={handleFrameLoad}
          />
        </Box>
      ) : isLoading ? (
        <Box className='email-preview__body email-preview__body--loading'>
          <CircularProgress size={typography.fontSizePx.xl} />
        </Box>
      ) : (
        <Box className='email-preview__body'>
          {showEmailTemplate && (
            <Box className='email-preview__template-header'>
              {orgLogoUrl ? (
                <img src={orgLogoUrl} alt={orgName} className='email-preview__template-logo' />
              ) : (
                <Avatar className='email-preview__template-avatar' alt={orgName} size='xl' />
              )}
              <Typography className='email-preview__template-org-name'>{orgName}</Typography>
              <Box className='email-preview__template-divider' />
              {subject && (
                <Typography className='email-preview__template-subject'>{subject}</Typography>
              )}
            </Box>
          )}

          {body ? (
            <Box
              className='email-preview__body-content'
              sx={PROSE_CONTENT_STYLES}
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(body) }}
            />
          ) : (
            <Typography className='email-preview__empty-message'>
              Your email preview will appear here
            </Typography>
          )}

          {showEmailTemplate && (
            <Box className='email-preview__template-footer'>
              <Box className='email-preview__template-footer-divider' />
              <Typography className='email-preview__template-footer-text'>
                You received this email as part of your request. This message was sent by
                Polimorphic on behalf of {orgName}.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {attachmentNames && attachmentNames.length > 0 && (
        <Box className='email-preview__attachments'>
          {attachmentNames.map((name, index) => (
            <Chip
              key={`${name}-${index}`}
              className='email-preview__attachment-chip'
              label={name}
              icon={<Icon name='attachFile' />}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}

export default EmailPreview
